'use client';

import { useEffect, useState } from 'react';

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    console.error('Global error:', error);
  }, [error]);

  const handleCopy = async () => {
    const text = [
      `Error: ${error.name}: ${error.message}`,
      error.digest ? `Digest: ${error.digest}` : '',
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
    ].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const errorCode = error.digest || `${error.name}: ${error.message}`;

  return (
    <html lang="en" className="dark">
      <body style={{ margin: 0, padding: 0, background: '#0f172a', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
        <div style={{ textAlign: 'center', maxWidth: 480, padding: '0 24px' }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#fff', margin: '0 0 12px' }}>Something broke</h1>
          <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.6, margin: '0 0 24px' }}>
            The app ran into an unexpected error. Your data is safe — try refreshing the page.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={reset}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#0ea5e9', color: '#fff', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer' }}
            >
              Try Again
            </button>
            <a
              href="/"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#1e293b', color: '#cbd5e1', fontSize: 14, fontWeight: 500, borderRadius: 8, border: '1px solid #334155', textDecoration: 'none' }}
            >
              Dashboard
            </a>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 24 }}>
            <button
              onClick={handleCopy}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: copied ? '#34d399' : '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'monospace' }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {copied ? (
                  <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>
                ) : (
                  <><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>
                )}
              </svg>
              {copied ? 'Copied!' : 'Copy error code'}
            </button>
            <span style={{ color: '#334155' }}>|</span>
            <button
              onClick={() => setShowDetails(!showDetails)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {showDetails ? '\u25B2 Hide details' : '\u25BC Show details'}
            </button>
          </div>

          {showDetails && (
            <div style={{ marginTop: 12, textAlign: 'left', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 16, maxHeight: 200, overflow: 'auto' }}>
              <p style={{ fontSize: 12, color: '#f87171', fontWeight: 600, margin: '0 0 4px' }}>{error.name}: {error.message}</p>
              {error.stack && (
                <pre style={{ fontSize: 10, color: '#64748b', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', lineHeight: 1.5, margin: 0 }}>
                  {error.stack}
                </pre>
              )}
              {error.digest && (
                <p style={{ fontSize: 10, color: '#475569', marginTop: 8, fontFamily: 'monospace' }}>Digest: {error.digest}</p>
              )}
            </div>
          )}
        </div>
      </body>
    </html>
  );
}
